import { useEffect, useState } from "react";
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Calendar, Send, BarChart3, LogOut, Sparkles, ImagesIcon, GaugeCircle, ShieldCheck, Bug, Users,
} from "lucide-react";
import clsx from "clsx";
import { getApiKey, setApiKey, clearApiKey } from "./api/client";
import { Dashboard } from "./pages/Dashboard";
import { ContentByDay } from "./pages/ContentByDay";
import { Publications } from "./pages/Publications";
import { AiCost } from "./pages/AiCost";
import { Login } from "./pages/Login";
import { MediaGallery } from "./pages/MediaGallery";
import { SignDetail } from "./pages/SignDetail";
import { Readiness } from "./pages/Readiness";
import { DataQuality } from "./pages/DataQuality";
import { Errors } from "./pages/Errors";
import { LunaraUsers } from "./pages/LunaraUsers";

const NAV = [
  { to: "/", label: "Дашборд", Icon: LayoutDashboard },
  { to: "/lunara-users", label: "Юзеры Лунары", Icon: Users },
  { to: "/readiness", label: "Готовность", Icon: GaugeCircle },
  { to: "/content", label: "Контент", Icon: Calendar },
  { to: "/quality", label: "Качество данных", Icon: ShieldCheck },
  { to: "/publications", label: "Публикации", Icon: Send },
  { to: "/ai-cost", label: "AI расходы", Icon: BarChart3 },
  { to: "/media", label: "Картинки", Icon: ImagesIcon },
  { to: "/errors", label: "Ошибки", Icon: Bug },
];

export function App() {
  const [authed, setAuthed] = useState<boolean>(() => !!getApiKey());
  const location = useLocation();

  useEffect(() => {
    // если ключ изменился в другой вкладке — обновим
    const onStorage = () => setAuthed(!!getApiKey());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!authed) {
    return <Login onSubmit={(key) => { setApiKey(key); setAuthed(true); }} />;
  }

  return (
    <div className="flex h-screen text-white/90">
      <aside className="w-64 shrink-0 border-r border-white/5 bg-black/30 backdrop-blur-md flex flex-col">
        <div className="px-6 py-5 flex items-center gap-2">
          <Sparkles className="text-astro-gold" size={22} />
          <span className="font-display text-lg font-bold tracking-tight">AstroV2</span>
        </div>
        <nav className="flex-1 px-2 space-y-1">
          {NAV.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                clsx(
                  "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition",
                  isActive
                    ? "bg-astro-violet/20 text-white border border-astro-violet/30"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                )
              }
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <button
          onClick={() => { clearApiKey(); setAuthed(false); }}
          className="mx-3 mb-3 px-4 py-2 text-sm text-white/50 hover:text-white/90 hover:bg-white/5 rounded-lg transition flex items-center gap-2"
        >
          <LogOut size={16} /> Выйти
        </button>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 max-w-7xl mx-auto">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/lunara-users" element={<LunaraUsers />} />
            <Route path="/readiness" element={<Readiness />} />
            <Route path="/content" element={<ContentByDay />} />
            <Route path="/quality" element={<DataQuality />} />
            <Route path="/publications" element={<Publications />} />
            <Route path="/ai-cost" element={<AiCost />} />
            <Route path="/media" element={<MediaGallery />} />
            <Route path="/errors" element={<Errors />} />
            <Route path="/sign/:date/:sign" element={<SignDetail />} />
            <Route path="*" element={<Navigate to="/" replace state={{ from: location }} />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
